import React, { useState } from 'react'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Typography } from '@mui/material'
import TermsAndConditionsPage from './TermsAndConditionsPage'
import VehicleIdentificationNumberPage from './VehicleIdentificationNumberPage'
import { DialogActions } from '@e3dc-react/shell/Elements/Dialog/Dialog'
import { Form, Formik } from 'formik'
import { FetchAuthUrlPayload } from '../../models'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { fetchAuthenticationUrl } from '../../api'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { getTranslationBasedOnEhaErrorCodes, isE3dcError } from '../../functions/errors.functions'
import { useManufacturerConnectionTermsFormInitializer } from '../../hooks'

export interface ManufacturerConnectionTerms {
  vin?: string
  accepted: boolean
}

export interface ManufacturerConnectionTermsProps {
  onClose?: () => void
  place?: number
  authUrlPayload: FetchAuthUrlPayload
  vinNeeded: boolean
  installationId: number
}

type Page = 1 | 2

const PAGES_AMOUNT = 2
const FIRST_PAGE: Page = 1
const SECOND_PAGE: Page = 2
/**
 * This component is a modal in which we can agree or cancel Terms and Conditions for manufacturer connection
 * If we agree we need to be navigated to the manufacturerUrl for authentication
 */
const ManufacturerConnectionTermsDialog: React.FC<ManufacturerConnectionTermsProps> = props => {
  const { onClose, authUrlPayload, vinNeeded, installationId } = props
  const { t } = useCustomTranslation()
  const [page, setPage] = useState<Page>(FIRST_PAGE)
  const pagination = `${page}/${PAGES_AMOUNT}`
  const authFetch = useAuthFetchWithResponse()
  const { enqueueErrorStack } = useSnackbar()
  const { initialValues, validationSchema } = useManufacturerConnectionTermsFormInitializer(vinNeeded)

  const onSubmit = async (values: ManufacturerConnectionTerms): Promise<void> => {
    try {
      const { error, value } = await fetchAuthenticationUrl(
        { ...authUrlPayload, ...(values.vin ? { vin: values.vin } : {}) },
        installationId,
        authFetch
      )
      if (error && isE3dcError(error)) {
        enqueueErrorStack(getTranslationBasedOnEhaErrorCodes(error.response?.data.errno))
      }
      if (value) window.open(value, '_self')
    } catch (error) {
      enqueueErrorStack(t('emobility.vendor-connection-terms-dialog.error-fetching-url'))
    }
  }

  const getPrimaryAction = (accepted: boolean, isValid: boolean, handleSubmit: () => void): DialogActions['primary'] => {
    if (vinNeeded && page === FIRST_PAGE)
      return {
        name: t('common.next'),
        onClick: () => setPage(SECOND_PAGE),
        disabled: !accepted,
      }
    return {
      name: t('common.agree'),
      onClick: handleSubmit,
      disabled: !accepted || !isValid,
    }
  }

  const getSecondaryAction = (): DialogActions['secondary'] => {
    if (vinNeeded && page === SECOND_PAGE) {
      /** Array is needed to prevent default closing of dialog */
      return [
        {
          name: t('common.back'),
          onClick: () => setPage(FIRST_PAGE),
        },
      ]
    }
    return { name: t('common.cancel') }
  }

  const getModalActions = (accepted: boolean, isValid: boolean, handleSubmit: () => void): DialogActions => ({
    primary: getPrimaryAction(accepted, isValid, handleSubmit),
    secondary: getSecondaryAction(),
  })

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
      {({ values, handleSubmit, isValid }) => {
        return (
          <Form
          //placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
          >
            <Dialog
              title={t('emobility.vendor-connection-terms-dialog.title')}
              onClose={onClose}
              actions={getModalActions(values.accepted, isValid, handleSubmit)}
              maxWidth="sm"
            >
              {page === FIRST_PAGE ? <TermsAndConditionsPage /> : <VehicleIdentificationNumberPage />}
              {vinNeeded && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Typography variant="caption">{pagination}</Typography>
                </Box>
              )}
            </Dialog>
          </Form>
        )
      }}
    </Formik>
  )
}

export default ManufacturerConnectionTermsDialog
